import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { UserCog } from "lucide-react";

/**
 * Ranking of sellers with client counts and trips this month.
 * Rendered only for admin users when at least one seller exists.
 */
export function SellerRankingCard({ users, clients, currentAdmin, now = new Date() }) {
  const isAdmin = (currentAdmin?.role || "admin") === "admin";
  const hasSellers = users.some((u) => (u.role || "admin") === "seller");
  if (!isAdmin || !hasSellers) return null;

  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const clientsBySeller = {};
  const tripsThisMonthBySeller = {};
  for (const c of clients) {
    const sid = c.seller_id || "__unassigned__";
    clientsBySeller[sid] = (clientsBySeller[sid] || 0) + 1;
    if (c.trip_date) {
      const td = new Date(c.trip_date);
      if (td >= monthStart && td < monthEnd) {
        tripsThisMonthBySeller[sid] = (tripsThisMonthBySeller[sid] || 0) + 1;
      }
    }
  }
  const rows = users.map((u) => ({
    id: u.id,
    name: u.full_name || u.username,
    role: u.role || "admin",
    clients: clientsBySeller[u.id] || 0,
    trips: tripsThisMonthBySeller[u.id] || 0,
  }));
  const unassignedCount = clientsBySeller["__unassigned__"] || 0;
  const unassignedTrips = tripsThisMonthBySeller["__unassigned__"] || 0;
  rows.sort((a, b) => b.clients - a.clients || b.trips - a.trips);
  const maxClients = Math.max(1, ...rows.map((r) => r.clients));

  return (
    <Card className="glass-card border-0 mt-5" data-testid="seller-ranking-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
          <UserCog className="w-5 h-5" />
          Ranking de vendedores
        </CardTitle>
        <CardDescription>
          Clientes cargados por cada vendedor y viajes este mes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 py-2 border-b border-sky-50 last:border-0"
              data-testid={`ranking-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sky-900 truncate">{r.name}</p>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      r.role === "admin"
                        ? "bg-sky-100 text-sky-800"
                        : "bg-emerald-100 text-emerald-800"
                    }`}
                  >
                    {r.role === "admin" ? "Admin" : "Vendedor"}
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-sky-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-sky-500 to-cyan-500"
                    style={{
                      width: `${Math.round((r.clients / maxClients) * 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sky-900 font-semibold text-sm">
                  {r.clients}{" "}
                  <span className="text-xs text-sky-600/70 font-normal">
                    clientes
                  </span>
                </p>
                <p className="text-xs text-sky-700/70">
                  {r.trips} viaje{r.trips === 1 ? "" : "s"} este mes
                </p>
              </div>
            </div>
          ))}
          {unassignedCount > 0 && (
            <div
              className="flex items-center justify-between gap-3 py-2 mt-1 border-t border-sky-100 text-sky-700/80"
              data-testid="ranking-unassigned"
            >
              <p className="text-sm italic">Sin vendedor asignado</p>
              <p className="text-xs">
                {unassignedCount} cliente{unassignedCount === 1 ? "" : "s"} ·{" "}
                {unassignedTrips} viaje{unassignedTrips === 1 ? "" : "s"} este mes
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
